/**
 * TulevaisuuskuvatGroup — the 4 scenario summary cards (Teknologia, Ilmasto,
 * Demokratia, Väestö) rendered in a 2×2 grid, with a shared ImageLightbox
 * that lets the reader click into any of them and chevron-browse the rest.
 *
 * Each card now also lists the 3 tulevaisuuskuva variants underneath the
 * image with a one-line description per variant. The variant that this
 * workshop chose is highlighted (border-left + bold title) so the reader
 * sees both the chosen path and what was on the table.
 *
 * Variant data is read from report_edits overlay:
 *   tulevaisuuskuvat.<scenario_key>.<1|2|3>.title
 *   tulevaisuuskuvat.<scenario_key>.<1|2|3>.description
 *
 * Long-term these belong in a canonical scenario_variants table since
 * they're identical across instances using the standard scenarios — but
 * for now overlay keeps them per-instance and editable.
 *
 * Reads URLs from report_edits via useReportEdits at the wrapper level
 * (so the gallery for the lightbox is built from the same source the
 * slots render from). Field keys use diacritic-stripped lowercase
 * scenario keys — same convention as `apps/backend/src/scripts/
 * populate-tulevaisuuskuvat.js`.
 */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import ReportImageSlot from './ReportImageSlot';
import ImageLightbox from './ImageLightbox';
import EditableText from './EditableText';
import { useReportEdits } from './ReportEditsContext';
import { normalizeImageUrl } from './imageUrl';

const SCENARIOS = [
  { name: 'Teknologia', key: 'teknologia' },
  { name: 'Ilmasto',    key: 'ilmasto' },
  { name: 'Demokratia', key: 'demokratia' },
  { name: 'Väestö',     key: 'vaesto' },
];

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin: 16px 0;
  width: 100%;
`;

const Cell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1 1 280px;
  min-width: 0;
  max-width: 100%;
`;

const VariantList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
`;

const Variant = styled.div`
  padding: 8px 10px;
  border-left: 3px solid ${p => p.$selected ? '#367ABA' : 'rgba(0,0,0,0.08)'};
  background: ${p => p.$selected ? 'rgba(54, 122, 186, 0.06)' : 'transparent'};
  border-radius: 0;
`;

const VariantTitle = styled.div`
  font-size: 12.5px;
  font-weight: ${p => p.$selected ? 700 : 600};
  color: ${p => p.$selected ? '#0c3257' : '#1f2937'};
  margin-bottom: 2px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const VariantDesc = styled.div`
  font-size: 12.5px;
  line-height: 1.5;
  color: #374151;
`;

// Strip diacritics + casing so "Hillintä onnistuu" matches
// "ilmastonmuutoksen hillintä onnistuu" — the K5 Ilmasto value carries
// the longer phrasing that scenarios.selected_tulevaisuuskuva uses, but
// the overlay variant titles are short. A normalised includes() catches
// both directions of partial-overlap.
function normalize(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}
function variantMatchesSelected(variantTitle, selected) {
  const a = normalize(variantTitle);
  const b = normalize(selected);
  if (!a || !b) return false;
  return a === b || b.includes(a) || a.includes(b);
}

export default function TulevaisuuskuvatGroup({ scenarios = [] }) {
  const { get } = useReportEdits();
  const [lightboxStart, setLightboxStart] = useState(null);

  // Map scenario_title → selected_tulevaisuuskuva, looked up once per render.
  // Used to mark the "this is what the workshop locked in" variant.
  const selectedByScenarioName = {};
  for (const s of scenarios) {
    if (s?.title && s?.selected_tulevaisuuskuva) {
      selectedByScenarioName[s.title] = s.selected_tulevaisuuskuva;
    }
  }

  // Build the gallery from whatever URLs are actually present. The lightbox
  // navigation only walks slots that have content, so the chevrons never
  // land on a blank slot.
  const gallery = [];
  for (const s of SCENARIOS) {
    const url = normalizeImageUrl(get(`tulevaisuuskuvat.${s.key}.image_url`, ''));
    if (!url) continue;
    const alt = get(`tulevaisuuskuvat.${s.key}.alt`, `Tulevaisuuskuva — ${s.name}`);
    gallery.push({ url, alt, caption: `Skenaario — ${s.name}` });
  }

  const openAt = ({ url }) => {
    const idx = gallery.findIndex((g) => g.url === url);
    setLightboxStart(idx >= 0 ? idx : 0);
  };

  return (
    <>
      {/* 2-up on desktop, 1-up on narrow viewports so the variant text
          has room to breathe. minmax(280px, …) collapses the layout
          before content overflows horizontally instead of squishing. */}
      <Grid>
        {SCENARIOS.map(({ name, key }) => {
          const selected = selectedByScenarioName[name] || '';
          return (
            <Cell key={key}>
              <ReportImageSlot
                fieldKeyPrefix={`tulevaisuuskuvat.${key}`}
                label={`Tulevaisuuskuva — ${name}`}
                aspect="16 / 9"
                fit="contain"
                hint={`Lisää ${name}-skenaarion tulevaisuuskuva (Kuvat/tulevaisuuskuvat_${name}.png)`}
                onImageClick={openAt}
              />
              <VariantList>
                {[1, 2, 3].map(idx => {
                  const titleKey = `tulevaisuuskuvat.${key}.${idx}.title`;
                  const descKey  = `tulevaisuuskuvat.${key}.${idx}.description`;
                  const title = get(titleKey, '');
                  const desc  = get(descKey, '');
                  // Drop the slot entirely if neither title nor description
                  // has been populated yet — keeps cards tight on instances
                  // that haven't filled these in.
                  if (!title && !desc) return null;
                  const isSelected = variantMatchesSelected(title, selected);
                  return (
                    <Variant key={idx} $selected={isSelected}>
                      <VariantTitle $selected={isSelected}>
                        <EditableText
                          fieldKey={titleKey}
                          fallback={title}
                          multiline={false}
                          label={`${name} — vaihtoehto ${idx} otsikko`}
                        />
                      </VariantTitle>
                      <VariantDesc>
                        <EditableText
                          fieldKey={descKey}
                          fallback={desc}
                          label={`${name} — vaihtoehto ${idx} kuvaus`}
                        />
                      </VariantDesc>
                    </Variant>
                  );
                })}
              </VariantList>
            </Cell>
          );
        })}
      </Grid>

      {lightboxStart !== null && gallery.length > 0 && (
        <ImageLightbox
          images={gallery}
          initialIndex={lightboxStart}
          onClose={() => setLightboxStart(null)}
        />
      )}
    </>
  );
}
